/**
 * Pure-data notification-type registry — no zod imports, no openapi
 * runtime, so this module is safe to re-export as a runtime value from
 * the `@quovy/types` barrel. It is the single source of truth for the
 * set of notification types, their display labels, and their grouping.
 *
 * Every hardcoded notification-type list in the codebase derives from
 * this registry:
 *   * the resolver's `NOTIFICATION_TYPES` (server)
 *   * the `NotificationType` z.enum (server wire schema + OpenAPI spec)
 *   * the `NotificationType` union (web `lib/api`)
 *   * the notifications settings page labels / order / group labels (web)
 *
 * Array order is significant: it is the settings-page display order AND
 * must match the OpenAPI `NotificationType` enum order so the spec
 * snapshot stays byte-stable. Add new types at the end unless the wire
 * order is deliberately changing.
 *
 * `group` equals the segment of the type before the "." (the object
 * type), and `groupLabel` is the human-readable header for that group.
 */

export type NotificationTypeMetadata = {
  /** Wire value, e.g. "workflow_run.completed". */
  type: string;
  /** Per-type display label for the settings page. */
  label: string;
  /** Object-type group key — the segment before the ".". */
  group: string;
  /** Human-readable header for the group. */
  groupLabel: string;
};

export const NOTIFICATION_TYPE_REGISTRY = [
  {
    type: "workflow_run.completed",
    label: "Workflow run completed",
    group: "workflow_run",
    groupLabel: "Workflow runs",
  },
  {
    type: "workflow_run.failed",
    label: "Workflow run failed",
    group: "workflow_run",
    groupLabel: "Workflow runs",
  },
  {
    type: "extraction.completed",
    label: "Extraction completed",
    group: "extraction",
    groupLabel: "Extraction",
  },
  {
    type: "extraction.failed",
    label: "Extraction failed",
    group: "extraction",
    groupLabel: "Extraction",
  },
  {
    type: "export.succeeded",
    label: "Export ready",
    group: "export",
    groupLabel: "Exports",
  },
  {
    type: "agent_thread.input_requested",
    label: "An agent needs your answer or approval",
    group: "agent_thread",
    groupLabel: "Agent threads",
  },
  {
    type: "bridge.offline",
    label: "A private bridge goes offline",
    group: "bridge",
    groupLabel: "Private bridges",
  },
  {
    type: "bridge.back_online",
    label: "A private bridge is back online",
    group: "bridge",
    groupLabel: "Private bridges",
  },
  {
    type: "task.assigned",
    label: "A task is assigned to you",
    group: "task",
    groupLabel: "Tasks",
  },
  {
    type: "webhook_endpoint.failing",
    label: "Deliveries to a webhook endpoint are failing",
    group: "webhook_endpoint",
    groupLabel: "Webhooks",
  },
  {
    type: "webhook_endpoint.disabled",
    label: "A webhook endpoint is turned off",
    group: "webhook_endpoint",
    groupLabel: "Webhooks",
  },
] as const;

export type NotificationType = (typeof NOTIFICATION_TYPE_REGISTRY)[number]["type"];

/**
 * Ordered tuple of the type strings, typed as a non-empty tuple so it can
 * drive `z.enum(...)` while preserving registry order.
 */
export const NOTIFICATION_TYPE_VALUES = NOTIFICATION_TYPE_REGISTRY.map((t) => t.type) as [
  NotificationType,
  ...NotificationType[],
];

/**
 * `status` values `GET /v1/unsubscribe` hands to the web app's `/unsubscribe`
 * page on its redirect. Shared so the API and the page cannot drift.
 */
export const NOTIFICATION_UNSUBSCRIBE_STATUSES = [
  "disabled",
  "already_disabled",
  "expired",
  "invalid",
  "unavailable",
] as const;

export type NotificationUnsubscribeStatus = (typeof NOTIFICATION_UNSUBSCRIBE_STATUSES)[number];
